import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Loader2, AlertCircle } from 'lucide-react';

// Icons
import { 
  CreditCard, 
  Shield, 
  CheckCircle, 
  XCircle, 
  Clock,
  ArrowLeft,
  Zap
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// =============================================================================
// PAYMENT SYSTEM STATUS CHECK - SHARED WITH SubscriptionComponents
// =============================================================================

let _paymentSystemReady = null;

const checkPaymentSystemReady = async () => {
  if (_paymentSystemReady !== null) {
    return _paymentSystemReady;
  }
  
  try {
    const response = await axios.get(`${API}/payments/status`);
    _paymentSystemReady = response.data.ready === true;
    
    if (!_paymentSystemReady) {
      console.error('Payment system not ready:', response.data.error);
    }
    
    return _paymentSystemReady;
  } catch (error) {
    console.error('Failed to check payment system status:', error);
    _paymentSystemReady = false;
    return false;
  }
};

// =============================================================================
// PAYMENT BUTTON COMPONENT — ONE-TIME PRODUCT PURCHASE (No packages/tiers)
// =============================================================================

// Payment Button Component
export const PaymentButton = ({ 
  itemType, 
  itemId, 
  price, 
  currency = "usd",
  title, 
  description,
  disabled = false,
  className = "",
  children
}) => {
  const [loading, setLoading] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(true);

  useEffect(() => {
    checkUserPurchase();
  }, [itemType, itemId]);

  const checkUserPurchase = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      if (!sessionToken) {
        setCheckingPurchase(false);
        return;
      }

      const response = await axios.get(`${API}/products/${itemType}/${itemId}/entitlement`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      setHasPurchased(response.data.entitled === true);
    } catch (error) {
      console.error('Error checking entitlement:', error);
    } finally {
      setCheckingPurchase(false);
    }
  };

  const handlePayment = async () => {
    if (hasPurchased) {
      toast.info('You already own this content!');
      return;
    }

    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      
      if (!sessionToken) {
        toast.error('Please log in to make a purchase');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/products/checkout`, {
        product_type: itemType,
        product_id: itemId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
      
    } catch (error) {
      console.error('Payment error:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Payment failed';
      toast.error(errorDetail);
      setLoading(false);
    }
  };

  if (checkingPurchase) {
    return (
      <Button disabled className={className} data-testid="purchase-checking-btn">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Checking...
      </Button>
    );
  }

  if (hasPurchased) {
    return (
      <Button disabled className={`${className} bg-green-500 hover:bg-green-600`} data-testid="purchase-owned-btn">
        <CheckCircle className="h-4 w-4 mr-2" />
        Purchased
      </Button>
    );
  }

  const currencySymbol = currency === "eur" ? "€" : "$";

  return (
    <Button 
      onClick={handlePayment}
      disabled={disabled || loading}
      className={`${className} bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600`}
      data-testid="purchase-buy-btn"
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Processing...
        </>
      ) : (
        <>
          <CreditCard className="h-4 w-4 mr-2" />
          {children || `Buy ${currencySymbol}${price}`}
        </>
      )}
    </Button>
  );
};

// Payment Success Page Component
export const PaymentSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId) {
      setStatus('missing');
      return;
    }
    verifySession(sessionId, 0);
  }, []);

  const verifySession = async (sessionId, attempt) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const sessionToken = Cookies.get('session_token');
      const { data } = await axios.get(`${API}/payments/verify-session/${sessionId}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setDetails(data);
      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.payment_status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => verifySession(sessionId, attempt + 1), 2500);
      }
    } catch (err) {
      console.error('Verify session error:', err);
      if (err.response?.status === 404) {
        setStatus('not_found');
      } else {
        setStatus('error');
      }
    }
  };

  const productLabel = details?.product_name
    || details?.product_type?.replace('_', ' ')
    || 'content';

  const downloadUrl = details?.has_downloads && details?.product_type && details?.product_id
    ? `/products/${details.product_type}/${details.product_id}`
    : null;

  const statusMap = {
    checking: {
      icon: <Loader2 className="h-14 w-14 animate-spin text-blue-500" />,
      title: 'Verifying Your Purchase',
      message: 'Please wait while we confirm your payment...',
      color: 'text-blue-600', bgColor: 'bg-blue-50'
    },
    success: {
      icon: <CheckCircle className="h-14 w-14 text-green-500" />,
      title: 'Payment Successful!',
      message: `You now have access to "${productLabel}".`,
      color: 'text-green-600', bgColor: 'bg-green-50'
    },
    expired: {
      icon: <Clock className="h-14 w-14 text-orange-500" />,
      title: 'Session Expired',
      message: 'This checkout session has expired. Please try purchasing again.',
      color: 'text-orange-600', bgColor: 'bg-orange-50'
    },
    timeout: {
      icon: <Clock className="h-14 w-14 text-yellow-500" />,
      title: 'Still Processing',
      message: 'Your payment is being processed. Check your email for confirmation, or visit your dashboard.',
      color: 'text-yellow-600', bgColor: 'bg-yellow-50'
    },
    not_found: {
      icon: <XCircle className="h-14 w-14 text-red-500" />,
      title: 'Session Not Found',
      message: 'This payment session could not be found. If you completed a purchase, check your dashboard.',
      color: 'text-red-600', bgColor: 'bg-red-50'
    },
    missing: {
      icon: <AlertCircle className="h-14 w-14 text-gray-400" />,
      title: 'No Session ID',
      message: 'No payment session was provided. Return to the dashboard to continue.',
      color: 'text-gray-600', bgColor: 'bg-gray-50'
    },
    error: {
      icon: <XCircle className="h-14 w-14 text-red-500" />,
      title: 'Something Went Wrong',
      message: 'We could not verify your payment. Please check your dashboard or contact support.',
      color: 'text-red-600', bgColor: 'bg-red-50'
    }
  };

  const s = statusMap[status] || statusMap.error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="payment-success-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">{s.icon}</div>
          <CardTitle className={`text-2xl ${s.color}`} data-testid="payment-status-title">
            {s.title}
          </CardTitle>
        </CardHeader>

        <CardContent className="text-center space-y-4">
          <div className={`p-4 rounded-lg ${s.bgColor}`}>
            <p className="text-gray-700" data-testid="payment-status-message">{s.message}</p>
          </div>

          {status === 'success' && details && (
            <div className="space-y-2 text-sm text-gray-600" data-testid="payment-details">
              {details.product_name && (
                <div className="flex justify-between">
                  <span>Product:</span>
                  <span className="font-semibold">{details.product_name}</span>
                </div>
              )}
              {details.creator_name && (
                <div className="flex justify-between">
                  <span>Creator:</span>
                  <span className="font-semibold">{details.creator_name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Amount:</span>
                <span className="font-semibold">${Number(details.amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Type:</span>
                <span className="font-semibold capitalize">{details.product_type?.replace('_', ' ')}</span>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-center mt-6">
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => window.location.href = '/dashboard'}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Dashboard</span>
            </Button>

            {status === 'success' && downloadUrl && (
              <Button
                data-testid="view-downloads-btn"
                onClick={() => window.location.href = downloadUrl}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              >
                <Zap className="h-4 w-4 mr-2" />
                View Downloads
              </Button>
            )}

            {status === 'success' && !downloadUrl && (
              <Button
                data-testid="access-content-btn"
                onClick={() => window.location.href = '/dashboard'}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              >
                <Zap className="h-4 w-4 mr-2" />
                Access Content
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Cancelled Page Component
export const PaymentCancelledPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <XCircle className="h-16 w-16 text-red-500" />
          </div>
          <CardTitle className="text-2xl text-red-600">
            Payment Cancelled
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-4">
          <div className="p-4 rounded-lg bg-red-50">
            <p className="text-gray-700">
              Your payment was cancelled. No charges were made to your account.
            </p>
          </div>

          <div className="flex gap-2 justify-center mt-6">
            <Button 
              onClick={() => window.location.href = '/dashboard'}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Dashboard</span>
            </Button>
            
            <Button 
              onClick={() => window.history.back()}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Purchase History Component
export const PurchaseHistory = ({ open, onClose }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchPurchases();
    }
  }, [open]);

  const fetchPurchases = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/user/purchases`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5 text-green-600" />
            <span>Purchase History</span>
          </DialogTitle>
          <DialogDescription>
            View all your purchased fitness content
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : purchases.length === 0 ? (
          <div className="text-center py-8">
            <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No purchases yet</p>
            <p className="text-sm text-gray-500">Start exploring and purchase some fitness content!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase, index) => (
              <Card key={index} className="border-green-100">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-green-100 text-green-700">
                          {purchase.item_type.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                      <p className="font-medium text-gray-900">Item ID: {purchase.item_id}</p>
                      <p className="text-sm text-gray-600">
                        Purchased: {new Date(purchase.purchase_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">${purchase.amount_paid}</p>
                      <p className="text-xs text-gray-500">Paid</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Payment Security Badge Component
export const PaymentSecurityBadge = () => {
  return (
    <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
      <Shield className="h-4 w-4 text-green-500" />
      <span>Secured by Stripe - Your payment information is encrypted and secure</span>
    </div>
  );
};

// Product Downloads Component
export const ProductDownloads = ({ productType, productId }) => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDownloads();
  }, [productType, productId]);

  const fetchDownloads = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      if (!sessionToken) { setLoading(false); return; }
      const response = await axios.get(`${API}/products/${productType}/${productId}/downloads`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setDownloads(response.data.downloads || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('purchase_required');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (error === 'purchase_required' || downloads.length === 0) return null;

  return (
    <div className="mt-4 border rounded-lg p-4 bg-gray-50" data-testid="product-downloads">
      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
        <ArrowLeft className="h-4 w-4 rotate-[135deg]" />
        Downloads
      </h4>
      <div className="space-y-2">
        {downloads.map((dl, i) => (
          <a
            key={i}
            href={dl.url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="flex items-center justify-between p-2 bg-white rounded border hover:border-blue-400 transition-colors text-sm"
            data-testid={`download-item-${i}`}
          >
            <span className="truncate mr-2">{dl.name}</span>
            <Badge variant="outline" className="shrink-0 text-xs">{dl.type}</Badge>
          </a>
        ))}
      </div>
    </div>
  );
};